/**
 * FOLLOWER BOOST — Hero growth sequencer (decorative, illustrative numbers only)
 *
 * One event model for every viewport:
 *   network event (likes, followers, views, …) →
 *     1. icon reaction (pop + network glow, TikTok glitch …)
 *     2. badge "+N label" anchored to the icon (desktop: in the orbit; mobile: stage layer, clamped)
 *     3. network particles (Instagram hearts, Facebook thumbs, Spotify equalizer, live dots)
 *     4. phone reaction, coherent with the event:
 *          followers / subscribers → the follower counter counts up by exactly +N
 *          views / impressions / streams / viewers → the growth chart pulses up
 *          likes / members → "recent activity" reacts (+N)
 *     5. clean exit, next network.
 *
 * Mobile: max 3 badges at once, never over the phone, never outside the stage, never overlapping
 * each other; hidden networks rotate in *in the slot of the one leaving* so icons stay in safe zones.
 *
 * Motion is CSS (transform/opacity). Timers stop off-screen / hidden tab; nothing runs with
 * prefers-reduced-motion or on low-power devices (static composition remains).
 */

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
const MOBILE = window.matchMedia('(max-width: 749px)');
const LOW_POWER =
  (navigator.connection && navigator.connection.saveData) ||
  (navigator.deviceMemory && navigator.deviceMemory <= 2) ||
  (MOBILE.matches && navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2);

/* type: what the badge says · dash: which part of the phone reacts */
const EVENTS = {
  instagram: [
    { type: 'likes', lo: 12, hi: 180, dash: 'activity', icon: 'heart', fx: 'hearts' },
    { type: 'followers', lo: 25, hi: 240, dash: 'counter', icon: 'users' },
    { type: 'views', lo: 800, hi: 6400, dash: 'chart', icon: 'eye' },
  ],
  tiktok: [
    { type: 'views', lo: 1200, hi: 9800, dash: 'chart', icon: 'eye', fx: 'glitch' },
    { type: 'followers', lo: 40, hi: 380, dash: 'counter', icon: 'users', fx: 'glitch' },
    { type: 'likes', lo: 60, hi: 900, dash: 'activity', icon: 'heart', fx: 'glitch' },
  ],
  youtube: [
    { type: 'views', lo: 800, hi: 4900, dash: 'chart', icon: 'play' },
    { type: 'subscribers', lo: 10, hi: 120, dash: 'counter', icon: 'users' },
  ],
  snapchat: [{ type: 'views', lo: 300, hi: 2400, dash: 'chart', icon: 'eye' }],
  facebook: [{ type: 'likes', lo: 10, hi: 90, dash: 'activity', icon: 'thumb', fx: 'thumbs' }],
  twitch: [{ type: 'viewers', lo: 120, hi: 980, dash: 'chart', icon: 'live' }],
  spotify: [{ type: 'streams', lo: 400, hi: 3600, dash: 'chart', icon: 'eq' }],
  linkedin: [{ type: 'impressions', lo: 300, hi: 2800, dash: 'chart', icon: 'trend' }],
  x: [
    { type: 'impressions', lo: 2000, hi: 9000, dash: 'chart', icon: 'trend' },
    { type: 'likes', lo: 20, hi: 200, dash: 'activity', icon: 'heart' },
  ],
  discord: [{ type: 'members', lo: 15, hi: 160, dash: 'activity', icon: 'live' }],
};

const SVG = {
  heart: '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 21s-7.5-4.6-9.6-9.2C.9 8.4 3 4.5 6.7 4.5c2.1 0 3.6 1.2 4.3 2.4.7-1.2 2.2-2.4 4.3-2.4 3.7 0 5.8 3.9 4.3 7.3C19.5 16.4 12 21 12 21z"/></svg>',
  users: '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8"/></svg>',
  eye: '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></svg>',
  play: '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M8 5.5v13l11-6.5z"/></svg>',
  thumb: '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 10v11H3V10h4zm0 0 4-8a3 3 0 0 1 3 3v4h6a2 2 0 0 1 2 2.3l-1.4 8A2 2 0 0 1 18.6 21H7"/></svg>',
  trend: '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m22 7-8.5 8.5-5-5L2 17"/><path d="M16 7h6v6"/></svg>',
  live: '<i class="fb-ev__live" aria-hidden="true"></i>',
  eq: '<i class="fb-ev__eq" aria-hidden="true"><b></b><b></b><b></b><b></b></i>',
};

const rand = (a, b) => a + Math.random() * (b - a);
const pick = (arr, not) => {
  if (!arr.length) return null;
  if (arr.length < 2) return arr[0];
  let v;
  do v = arr[Math.floor(Math.random() * arr.length)];
  while (v === not);
  return v;
};
const LANG = (document.documentElement.lang || 'fr').slice(0, 2);
const DEC = LANG === 'fr' ? ',' : '.';
const THOU = LANG === 'fr' ? ' ' : ',';
const fmtInt = (n) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, THOU);
const fmtK = (n) => (n >= 1000 ? (n / 1000).toFixed(1).replace('.0', '').replace('.', DEC) + 'K' : String(Math.round(n)));
const rectIn = (r, box) => ({ l: r.left - box.left, t: r.top - box.top, r: r.right - box.left, b: r.bottom - box.top, w: r.width, h: r.height });
const overlap = (a, b, pad = 4) => !(a.r + pad <= b.l || b.r + pad <= a.l || a.b + pad <= b.t || b.b + pad <= a.t);

class FbHeroFx {
  constructor(el) {
    this.el = el;
    this.stage = el.querySelector('[data-fb-stage]');
    this.orbits = [...el.querySelectorAll('.fb-orbit')];
    this.phone = el.querySelector('[data-fb-phone]');
    this.notifs = el.querySelector('[data-fb-notifs]');
    this.value = el.querySelector('[data-fb-live]');
    this.line = el.querySelector('[data-fb-live-line]');
    this.area = el.querySelector('[data-fb-live-area]');
    this.dot = el.querySelector('[data-fb-live-dot]');
    this.chart = el.querySelector('.fb-phone__chart');
    this.activity = el.querySelector('.fb-phone__card--activity');
    this.activityCount = el.querySelector('.fb-phone__avatars em');
    this.timers = [];
    this.running = false;
    this.lastOrbit = null;
    this.badges = [];

    try {
      this.labels = JSON.parse(el.querySelector('[data-fb-ev-labels]')?.textContent || '{}');
    } catch {
      this.labels = {};
    }

    // remember each orbit's home slot so mobile rotation can swap slots safely
    this.orbits.forEach((o) => {
      o.dataset.homeX = o.style.getPropertyValue('--x');
      o.dataset.homeY = o.style.getPropertyValue('--y');
    });

    if (LOW_POWER) el.classList.add('is-low-power');
    if (REDUCED.matches || LOW_POWER) return;

    this.layer = document.createElement('div');
    this.layer.className = 'fb-evlayer';
    this.layer.setAttribute('aria-hidden', 'true');
    this.stage?.appendChild(this.layer);

    this.ys = [82, 70, 74, 56, 62, 42, 46, 26, 16, 12];
    this.xs = [8, 34, 60, 86, 112, 138, 164, 190, 216, 232];
    this.live = this.value ? parseInt(this.value.dataset.fbLive, 10) || 0 : 0;
    this.activityN = this.activityCount ? parseInt(this.activityCount.textContent.replace(/\D/g, ''), 10) || 0 : 0;
    this.liveStarted = false;

    const io = new IntersectionObserver(
      (entries) => {
        this.visible = entries[0].isIntersecting;
        if (this.visible && !document.hidden) this.start();
        else this.stop();
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stop();
      else if (this.visible) this.start();
    });
    MOBILE.addEventListener?.('change', () => this.resetSlots());
  }

  /* ---------- lifecycle ---------- */
  start() {
    if (this.running) return;
    this.running = true;
    this.schedule(() => this.tickOrganic(), this.liveStarted ? rand(1800, 2600) : 2600);
    this.schedule(() => this.tickEvent(), rand(1200, 1800));
    if (!MOBILE.matches) this.schedule(() => this.tickNotif(), rand(4200, 5600));
    this.schedule(() => this.tickPresence(), rand(5200, 7000));
  }

  stop() {
    this.running = false;
    this.timers.forEach(clearTimeout);
    this.timers = [];
  }

  schedule(fn, ms) {
    if (!this.running) return;
    const id = setTimeout(() => {
      this.timers = this.timers.filter((t) => t !== id);
      if (this.running) fn();
    }, ms);
    this.timers.push(id);
  }

  later(fn, ms) {
    // fire-and-forget cleanup that must run even if the sequencer stops
    setTimeout(fn, ms);
  }

  visibleOrbits() {
    return this.orbits.filter((o) => !o.classList.contains('is-away') && getComputedStyle(o).display !== 'none' && EVENTS[o.dataset.fx]);
  }

  /* ---------- phone: organic growth between events (small, the events do the storytelling) ---------- */
  tickOrganic() {
    this.liveStarted = true;
    this.bumpCounter(Math.round(rand(1, 4)), false);
    if (Math.random() < 0.35) this.pushPoint(rand(-3, 5));
    this.schedule(() => this.tickOrganic(), rand(2600, 4200));
  }

  bumpCounter(n, loud) {
    if (!this.value || !this.live) return;
    const from = this.live;
    const to = from + n;
    this.live = to;
    const t0 = performance.now();
    const dur = loud ? 900 : 300;
    const step = (now) => {
      const k = Math.min(1, (now - t0) / dur);
      const e = 1 - Math.pow(1 - k, 3);
      this.value.textContent = fmtInt(from + (to - from) * e);
      if (k < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
    this.value.classList.remove('is-tick', 'is-boost');
    void this.value.offsetWidth;
    this.value.classList.add(loud ? 'is-boost' : 'is-tick');
    if (loud) this.flashDelta(this.value, '+' + fmtInt(n));
  }

  flashDelta(anchor, text) {
    const card = anchor.closest('.fb-phone__card');
    if (!card) return;
    const d = document.createElement('span');
    d.className = 'fb-phone__flash';
    d.textContent = text;
    card.appendChild(d);
    this.later(() => d.remove(), 1400);
  }

  pushPoint(delta) {
    if (!this.line) return;
    const last = this.ys[this.ys.length - 1];
    let next = last - delta;
    if (next < 10) next = rand(30, 46);
    next = Math.max(10, Math.min(92, next));
    this.ys = [...this.ys.slice(1), next];
    const pts = this.xs.map((x, i) => `${x} ${this.ys[i].toFixed(1)}`);
    const d = 'M' + pts.join(' L');
    this.line.setAttribute('d', d);
    if (this.area) this.area.setAttribute('d', `${d} L232 104 L8 104 Z`);
    if (this.dot) this.dot.setAttribute('cy', next.toFixed(1));
  }

  reactChart() {
    this.pushPoint(rand(10, 18));
    if (!this.chart) return;
    this.chart.classList.remove('is-pulse');
    void this.chart.getBoundingClientRect();
    this.chart.classList.add('is-pulse');
  }

  reactActivity(n) {
    if (!this.activity) return;
    this.activityN += Math.max(1, Math.round(n / 10));
    if (this.activityCount) this.activityCount.textContent = '+' + fmtK(this.activityN);
    this.activity.classList.remove('is-react');
    void this.activity.offsetWidth;
    this.activity.classList.add('is-react');
  }

  /* ---------- network events ---------- */
  tickEvent() {
    const orbit = pick(this.visibleOrbits(), this.lastOrbit);
    if (orbit) {
      this.lastOrbit = orbit;
      const list = EVENTS[orbit.dataset.fx];
      const ev = list[Math.floor(Math.random() * list.length)];
      const n = Math.round(rand(ev.lo, ev.hi));
      this.fire(orbit, ev, n);
    }
    const mobile = MOBILE.matches;
    this.schedule(() => this.tickEvent(), mobile ? rand(1300, 2000) : rand(1700, 2900));
  }

  fire(orbit, ev, n) {
    // 1-2. icon reaction + network glow
    orbit.dataset.ev = ev.fx || '';
    orbit.classList.remove('is-fx');
    void orbit.offsetWidth;
    orbit.classList.add('is-fx');
    this.later(() => orbit.classList.remove('is-fx'), 1500);

    // 3-4. badge + particles
    const text = (ev.type === 'followers' || ev.type === 'subscribers' || ev.type === 'members' ? '+' + fmtInt(n) : '+' + fmtK(n));
    const label = this.labels[ev.type] || ev.type;
    if (MOBILE.matches) this.badgeMobile(orbit, ev, text, label);
    else this.badgeDesktop(orbit, ev, text, label);

    // 5. phone reaction (slightly delayed: the event "travels" to the phone)
    this.later(() => {
      if (!this.running) return;
      if (ev.dash === 'counter') this.bumpCounter(n, true);
      else if (ev.dash === 'chart') this.reactChart();
      else this.reactActivity(n);
    }, 520);
  }

  badgeHTML(ev, text, label) {
    const icon = SVG[ev.icon] || '';
    return `<span class="fb-ev__icon fb-ev__icon--${ev.icon}">${icon}</span><span class="fb-ev__txt"><b>${text}</b><small>${label}</small></span>`;
  }

  particles(host, ev) {
    if (ev.fx !== 'hearts' && ev.fx !== 'thumbs') return;
    const glyph = ev.fx === 'hearts' ? SVG.heart : SVG.thumb;
    for (let i = 0; i < 3; i++) {
      const p = document.createElement('i');
      p.className = `fb-ev__particle fb-ev__particle--${ev.fx}`;
      p.innerHTML = glyph;
      p.style.setProperty('--d', `${i * 180}ms`);
      p.style.setProperty('--x', `${rand(-18, 18).toFixed(0)}px`);
      p.style.setProperty('--drift', `${rand(-16, 16).toFixed(0)}px`);
      host.appendChild(p);
      this.later(() => p.remove(), 2200);
    }
  }

  /* desktop: chip inside the orbit (moves with the icon in 3D) */
  badgeDesktop(orbit, ev, text, label) {
    const slot = orbit.querySelector('[data-fb-fx]');
    if (!slot) return;
    slot.innerHTML = '';
    const chip = document.createElement('span');
    chip.className = `fb-fx__chip fb-ev fb-ev--${orbit.dataset.fx}`;
    chip.innerHTML = this.badgeHTML(ev, text, label);
    slot.appendChild(chip);
    this.placeDesktopChip(slot, chip);
    this.particles(slot, ev);
    this.later(() => {
      if (slot.contains(chip)) slot.innerHTML = '';
    }, 3000);
  }

  /* desktop: pick the anchoring (centred / outward / inward) that keeps the chip off the phone and on screen */
  placeDesktopChip(slot, chip) {
    const o = slot.getBoundingClientRect();
    const w = chip.offsetWidth;
    const h = chip.offsetHeight;
    const phoneEl = this.phone?.querySelector('.fb-phone__body') || this.phone;
    const ph = phoneEl ? phoneEl.getBoundingClientRect() : null;
    const hero = this.el.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const top = o.top - 14 - 40; // chip rises ~40px during its life
    const bottom = o.top - 14 + h;
    const outward = o.left > (ph ? (ph.left + ph.right) / 2 : vw / 2) ? -0.12 : -0.88;
    const options = [-0.5, outward, outward === -0.12 ? -0.88 : -0.12];
    const fits = (tx, t, b) => {
      const l = o.left + tx * w;
      const r = l + w;
      const onPhone = ph && !(r + 6 <= ph.left || l - 6 >= ph.right || b + 4 <= ph.top || t - 4 >= ph.bottom);
      return { ok: !onPhone && l >= 8 && r <= vw - 8, onScreen: l >= 8 && r <= vw - 8 };
    };
    const iconH = slot.parentElement?.offsetHeight || 64;
    const belowTop = o.top + iconH + 6;
    const belowBottom = belowTop + h + 22;
    let best = null;
    let below = top < hero.top + 8;
    for (const tx of options) {
      if (!below && fits(tx, top, bottom).ok) {
        best = tx;
        break;
      }
    }
    if (best === null) {
      // above the icon touches the phone (or the header): open below the icon instead
      for (const tx of options) {
        if (fits(tx, belowTop, belowBottom).ok) {
          best = tx;
          below = true;
          break;
        }
      }
    }
    if (best === null) best = options.find((tx) => fits(tx, top, bottom).onScreen) ?? -0.5;
    chip.style.translate = `${best * 100}% 0`;
    if (below) {
      chip.classList.add('fb-ev--below');
      chip.style.setProperty('--fb-chip-drop', `${iconH + 6 + h}px`);
    }
  }

  /* mobile: flat layer over the stage, anchored under/over the icon, clamped and collision-free */
  badgeMobile(orbit, ev, text, label) {
    if (!this.layer || !this.stage) return;
    this.badges = this.badges.filter((b) => b.isConnected);
    if (this.badges.length >= 3) return;

    const box = this.stage.getBoundingClientRect();
    const W = box.width;
    const H = box.height;
    const pad = 6;
    const iconEl = orbit.querySelector('.fb-icon3d, .fb-tile') || orbit;
    const ic = rectIn(iconEl.getBoundingClientRect(), box);
    // The phone sways in 3D: exclude a fixed column around its untransformed width instead of its live box.
    const phoneEl = this.phone?.querySelector('.fb-phone__body') || this.phone;
    const pw = phoneEl?.offsetWidth || W * 0.38;
    const live = phoneEl ? rectIn(phoneEl.getBoundingClientRect(), box) : { l: W, r: 0 };
    const ph = { l: Math.min(live.l, W / 2 - pw / 2) - 10, r: Math.max(live.r, W / 2 + pw / 2) + 10 };
    const leftSide = ic.l + ic.w / 2 < W / 2;
    const colMin = leftSide ? pad : ph.r;
    const colMax = leftSide ? ph.l : W - pad;

    const b = document.createElement('span');
    b.className = `fb-ev fb-ev--m fb-ev--${orbit.dataset.fx}`;
    b.innerHTML = this.badgeHTML(ev, text, label);
    b.style.visibility = 'hidden';
    this.layer.appendChild(b);
    // progressive fit: stacked badge → without label (icon + number)
    if (b.offsetWidth > colMax - colMin) b.classList.add('fb-ev--nolabel');
    const w = b.offsetWidth;
    const h = b.offsetHeight;

    let x = ic.l + ic.w / 2 - w / 2;
    x = Math.max(colMin, Math.min(colMax - w, x));
    if (colMax - colMin < w) x = leftSide ? pad : W - w - pad;

    // obstacles: other visible icons (soft) and live badges (hard)
    const icons = this.orbits
      .filter((o) => o !== orbit && getComputedStyle(o).display !== 'none' && !o.classList.contains('is-away'))
      .map((o) => rectIn((o.querySelector('.fb-icon3d, .fb-tile') || o).getBoundingClientRect(), box));
    const others = this.badges.map((o) => ({ l: o._x, t: o._y, r: o._x + o.offsetWidth, b: o._y + o.offsetHeight }));
    const candidates = [ic.b + 4, ic.t - h - 4, ic.t + ic.h / 2 - h / 2, ic.b + h + 10, ic.t - 2 * h - 10]
      .map((y) => Math.round(y))
      .filter((y) => y >= pad && y + h <= H - pad);
    let best = null;
    let bestScore = Infinity;
    candidates.forEach((y, i) => {
      const r = { l: x, t: y, r: x + w, b: y + h };
      if (others.some((o) => overlap(r, o))) return;
      const hits = icons.filter((o) => overlap(r, o, 0)).length;
      const score = hits * 10 + i;
      if (score < bestScore) {
        bestScore = score;
        best = y;
      }
    });
    if (best === null) {
      b.remove();
      return;
    }
    b._x = x;
    b._y = best;
    // left/top (not transform): the CSS pop uses `scale`, which would otherwise scale the offset too
    b.style.left = `${Math.round(x)}px`;
    b.style.top = `${best}px`;
    b.style.visibility = '';
    b.classList.add('is-in');
    this.badges.push(b);
    this.particles(b, ev);
    this.later(() => b.classList.add('is-out'), 2700);
    this.later(() => b.remove(), 3200);
  }

  /* ---------- micro-notifications near the phone (desktop only) ---------- */
  tickNotif() {
    const tpl = this.el.querySelector('[data-fb-notif-tpl]');
    if (this.notifs && tpl && !this.notifs.firstChild && !MOBILE.matches) {
      const items = [...tpl.content.children];
      const n = items[Math.floor(Math.random() * items.length)].cloneNode(true);
      n.style.top = `${rand(0, 40).toFixed(0)}%`;
      this.notifs.appendChild(n);
      this.later(() => n.remove(), 3200);
    }
    if (!MOBILE.matches) this.schedule(() => this.tickNotif(), rand(6500, 10000));
  }

  /* ---------- entrances / exits / mobile rotation ---------- */
  resetSlots() {
    this.orbits.forEach((o) => {
      o.style.setProperty('--x', o.dataset.homeX);
      o.style.setProperty('--y', o.dataset.homeY);
      o.classList.remove('is-cycled', 'is-away', 'is-parked');
      delete o.dataset.ev;
    });
  }

  tickPresence() {
    if (MOBILE.matches) {
      const shown = this.orbits.filter((o) => getComputedStyle(o).display !== 'none' && !o.classList.contains('is-away'));
      const pool = this.orbits.filter((o) => getComputedStyle(o).display === 'none');
      if (pool.length && shown.length > 3) {
        const out = pick(shown.filter((o) => o !== this.lastOrbit));
        const inn = pool[Math.floor(Math.random() * pool.length)];
        if (out && inn) {
          out.classList.add('is-away');
          this.later(() => {
            // the incoming network takes the exact slot of the leaving one (safe, tested positions)
            inn.style.setProperty('--x', out.style.getPropertyValue('--x'));
            inn.style.setProperty('--y', out.style.getPropertyValue('--y'));
            if (out.dataset.lane) inn.dataset.lane = out.dataset.lane;
            out.classList.remove('is-cycled', 'is-away');
            out.style.setProperty('--x', out.dataset.homeX);
            out.style.setProperty('--y', out.dataset.homeY);
            out.classList.add('is-parked');
            inn.classList.remove('is-parked');
            inn.classList.add('is-cycled', 'is-away');
            requestAnimationFrame(() => requestAnimationFrame(() => inn.classList.remove('is-away')));
          }, 950);
        }
      }
    } else {
      const shown = this.visibleOrbits().filter((o) => !o.querySelector('.fb-badge') && o !== this.lastOrbit);
      const o = pick(shown);
      if (o) {
        o.classList.add('is-away');
        this.later(() => o.classList.remove('is-away'), rand(2600, 4200));
      }
    }
    this.schedule(() => this.tickPresence(), rand(7000, 10500));
  }
}

const boot = () => {
  document.querySelectorAll('[data-fb-hero]').forEach((el) => {
    if (!el.__fbHeroFx) el.__fbHeroFx = new FbHeroFx(el);
  });
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
document.addEventListener('shopify:section:load', boot);
